import Icon from "@react-native-vector-icons/material-design-icons";
import FileIcon from "assets/images/defaultIcons/file.svg";
import theme from "assets/theme";
import React, { useCallback, useContext, useMemo, useRef } from "react";
import { Image, Pressable, StyleSheet, View } from "react-native";
import { appIconSize } from "web/constants";
import PressableContainer from "webComponentsFormElements/PressableContainer.component";
import { OrganizationContext } from "webContext/Organization.context";
import { ToastContext } from "webContext/Toast.context";

const classes = StyleSheet.create({
  imageContainerWrapper: {
    alignItems: "center",
  },
  editIconContainer: {
    position: "absolute",
    zIndex: 1000,
  },
  editIcon: {
    opacity: 0.8,
  },
  deleteIcon: {
    position: "absolute",
    top: -4,
    right: -4,
    opacity: 0.75,
  },
  htmlInput: {
    display: "none",
  },
});

const resizeImageToFit = (file, maxSizeBytes, callback) => {
  const image = new window.Image();
  const reader = new FileReader();

  reader.onload = (onloadEvent) => {
    image.onload = () => {
      const width = image.width;
      const height = image.height;
      let scale = 1.0;
      const canvas = document.createElement("canvas");
      const context = canvas.getContext("2d");

      const maxDimension = 500;
      if (width > maxDimension || height > maxDimension) {
        scale = maxDimension / Math.max(width, height);
      }

      function tryResize() {
        canvas.width = Math.round(width * scale);
        canvas.height = Math.round(height * scale);
        context.clearRect(0, 0, canvas.width, canvas.height);
        context.drawImage(image, 0, 0, canvas.width, canvas.height);

        canvas.toBlob((blob) => {
          if (blob.size <= maxSizeBytes || Math.min(width, height) < 50) {
            callback(blob);
          } else {
            scale *= 0.95;
            tryResize();
          }
        }, file.type);
      }
      tryResize();
    };
    image.src = onloadEvent.target.result;
  };
  reader.readAsDataURL(file);
};

export default function FileUpload(props) {
  const {
    placeholderIcon: PlaceholderIcon,
    file,
    setFile,
    setFileBlob,
    onChange,
    isDocumentUpload,
    canDelete = true,
    disabled,
    signedUrl,
    sizeMultiplier = { width: 1.1, height: 1.1 },
    accept,
  } = props;

  const { showToast } = useContext(ToastContext);

  const { organizationColor } = useContext(OrganizationContext);

  const stateClasses = useMemo(
    () =>
      StyleSheet.create({
        icon: {
          width: appIconSize * 1.1,
          height: appIconSize * 1.1,
          opacity: 0.4,
          fill: organizationColor,
        },
        image: {
          width: appIconSize * sizeMultiplier.width,
          height: appIconSize * sizeMultiplier.height,
          opacity: 0.4,
        },
        imageContainer: {
          width: appIconSize * sizeMultiplier.width,
          height: appIconSize * sizeMultiplier.height,
          justifyContent: "center",
          alignItems: "center",
        },
      }),
    [organizationColor, sizeMultiplier],
  );

  const inputRef = useRef(null);

  const handleInputChange = useCallback(
    async (event) => {
      const eventFile = event.target.files?.[0];
      if (!eventFile) {
        return;
      }

      const maxSize = isDocumentUpload ? 1_000_000 : 2_000_000;

      if (
        !isDocumentUpload &&
        eventFile.type.startsWith("image/") &&
        eventFile.size > maxSize
      ) {
        resizeImageToFit(eventFile, maxSize, (resizedBlob) => {
          if (resizedBlob.size > maxSize) {
            return showToast(
              "The image you chose is too big, and could not be resized to be under the limit of 2MB.",
              "error",
            );
          } else {
            showToast(
              "Image exceeded the limit of 2MB, but it was resized to meet this limit. Resize or compress manually for better quality.",
              "info",
            );
          }

          setFileBlob?.(resizedBlob);

          const fileReader = new FileReader();
          fileReader.readAsDataURL(resizedBlob);
          fileReader.onload = () => {
            setFile?.(fileReader.result);
            onChange?.(); // Call onChange after preview is ready
          };
        });
        return;
      } else if (eventFile.size > maxSize) {
        return showToast(
          `The file you choose is too big, the maximum size is ${isDocumentUpload ? "1MB" : "2MB"}.`,
          "error",
        );
      }

      setFileBlob?.(eventFile);

      const fileReader = new FileReader();
      fileReader.readAsDataURL(eventFile);
      fileReader.onload = () => {
        setFile?.(fileReader.result);
        onChange?.(); // Call onChange after preview is ready
      };
    },
    [isDocumentUpload, onChange, setFile, setFileBlob, showToast],
  );

  const handleDeletePress = useCallback(() => {
    setFile?.(null);
    setFileBlob?.(null);
    onChange?.();
  }, [setFile, setFileBlob, onChange]);

  return (
    <View style={classes.imageContainerWrapper}>
      <Pressable onPress={() => !disabled && inputRef.current.showPicker()}>
        <View style={stateClasses.imageContainer}>
          {!disabled && (
            <View style={classes.editIconContainer}>
              <Icon
                name="pencil-outline"
                color={theme.color.greyHighlight}
                style={classes.editIcon}
                size={appIconSize * 0.75}
              />
            </View>
          )}
          {signedUrl ? (
            <Image
              source={{ uri: signedUrl }}
              resizeMode="contain"
              style={stateClasses.image}
            />
          ) : (
            <></>
          )}
          {!isDocumentUpload && file && !signedUrl ? (
            <Image
              source={{ uri: file }}
              resizeMode="contain"
              style={stateClasses.image}
            />
          ) : PlaceholderIcon ? (
            <PlaceholderIcon style={stateClasses.icon} />
          ) : (
            <FileIcon style={stateClasses.icon} />
          )}
        </View>
        <input
          style={classes.htmlInput}
          type="file"
          accept={
            accept || (isDocumentUpload ? undefined : "image/png, image/jpeg")
          }
          onChange={handleInputChange}
          ref={inputRef}
        />
      </Pressable>
      {canDelete && !disabled && (
        <PressableContainer
          rootStyle={classes.deleteIcon}
          onPress={handleDeletePress}
        >
          <Icon name="close-thick" color={theme.color.red} size={12} />
        </PressableContainer>
      )}
    </View>
  );
}
